import type { PayloadAction, SerializedError } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { brandingService } from '@simwood/nv-react-components';
import { isElectron } from '../helpers/platform';
import { capitalize } from '../helpers/util';
import { fetchBranding, finaliseLogout } from '../store/thunks';
import { ApiBranding, AppInfo } from '../types/Branding';

interface ProcessedApp {
  name: string;
  icon: string;
  url: string;
}

export type BrandingState = Omit<
  ApiBranding,
  'branding' | 'apps' | 'productLogoSrc' | 'companyLogoSrc' | 'partnerId'
> &
  Partial<Pick<ApiBranding, 'branding'>> & {
    error?: SerializedError;
    isFetching: boolean;
    isInitialized: boolean;
    partnerLogoSrc: string;
    appLogoSrc: string;
    apps?: ProcessedApp[];
    favicons?: AppInfo['favicons'];
    kbLinkLocation?: string;
  };

const initialState: BrandingState = {
  isFetching: false,
  isInitialized: false,
  error: undefined,
  apiRoot: '',
  brand: '',
  website: '',
  partnerLogoSrc: '',
  appLogoSrc: '',
};

const brandingSlice = createSlice({
  name: 'branding',
  initialState,
  reducers: {
    brandingReset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBranding.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(fetchBranding.rejected, (state, action) => {
        state.isFetching = false;
        state.error = action.error;
      })
      .addCase(
        fetchBranding.fulfilled,
        (state, action: PayloadAction<ApiBranding>) => {
          const {
            branding,
            apps,
            apiRoot,
            brand,
            website,
            companyLogoSrc,
            productLogoSrc,
          } = action.payload;
          const { favicons } = apps.communicator;
          const processedApps: ProcessedApp[] = [];
          const iconBaseUrl =
            process.env.NODE_ENV === 'development'
              ? 'https://my.nimvelo.com'
              : '';
          Object.keys(apps).forEach((appName) => {
            const { color1, color2, url, hideFromMenu } = apps[appName];
            if (hideFromMenu) return;
            let iconUrl = `${iconBaseUrl}/branding/icons?name=${appName}`;
            iconUrl += `&color1=${color1.replace('#', '')}`;
            if (color2) {
              iconUrl += `&color2=${color2.replace('#', '')}`;
            }
            processedApps.push({
              name: capitalize(appName),
              icon: iconUrl,
              url,
            });
          });

          state.apps = processedApps;
          state.branding = branding;
          state.apiRoot = apiRoot;
          state.brand = brand;
          state.website = website;
          state.partnerLogoSrc = companyLogoSrc;
          state.appLogoSrc = productLogoSrc || companyLogoSrc;
          state.favicons = favicons;
          state.kbLinkLocation = apps.communicator.kbLinkLocation;
          state.isFetching = false;
          state.isInitialized = true;
        },
      )
      .addCase(finaliseLogout.fulfilled, () => {
        if (isElectron()) {
          brandingService.resetBranding(true);
          return initialState;
        }
      });
  },
});

export const { brandingReset } = brandingSlice.actions;

export default brandingSlice.reducer;
